import React, { useState, useEffect, useMemo } from 'react';
import { Form, Select, Typography, App, Table, Modal, Input, Button } from 'antd';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../data/base';
import { fetchAvailableLicenses } from '../../../services/api/cataloguesApi';
import catalogEvents, { CATALOG_EVENTS } from '../../../utils/catalogEvents';
import SubscriptionOptionRenderer from '../SubscriptionOptionRenderer';
import { greyScaleTokens, lightBaseTokens, primaryShades, statusTokens } from '../../../theme/theme';
import { sendUpgradeRequestEmail, getUserProfileForEmail } from '../../../services/emailService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Step component for selecting a catalogue and adding products
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.catalogues - List of available catalogues
 * @param {string} props.selectedCatalogue - ID of the selected catalogue
 * @param {Function} props.setSelectedCatalogue - Function to set the selected catalogue
 * @param {boolean} props.loading - Whether catalogues are loading
 * @param {Array} props.availableItemsData - Available items from the previous step
 * @param {Array} props.selectedRowKeys - Array of selected row keys (managed by parent)
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {Object} props.summary - Summary object with orderable and available items
 * @param {Function} props.onOrderAssets - Callback when Order Assets button is clicked
 * @param {string} props.selectedLicense - ID of the selected license
 * @param {Function} props.setSelectedLicense - Function to set the selected license
 * @param {string} props.initialCatalogId - ID of the catalog to pre-select, if any
 */
const SelectAddStep = ({
    catalogues,
    selectedCatalogue,
    setSelectedCatalogue,
    loading,
    availableItemsData = [],
    selectedRowKeys = [],
    onSelectionChange,
    summary = {},
    onOrderAssets,
    selectedLicense,
    setSelectedLicense,
    initialCatalogId
}) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();

    // Stato per i cataloghi
    const [catalogsData, setCatalogsData] = useState([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(false);

    // Stato per le licenze
    const [loadingLicenses, setLoadingLicenses] = useState(false);
    const [subscriptionSkuData, setSubscriptionSkuData] = useState({});

    // Stato per il modal di upgrade
    const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
    const [upgradeSubscription, setUpgradeSubscription] = useState(null);
    const [upgradeMessage, setUpgradeMessage] = useState('');
    const [sendingUpgradeRequest, setSendingUpgradeRequest] = useState(false);

    // Seleziona le licenze disponibili dal Redux store
    const availableLicenses = useSelector(state => state.licenses.availableLicenses);

    // Se arriviamo da un catalogo specifico, preseleziona la subscription corretta e disabilita il select
    useEffect(() => {
        if (initialCatalogId && availableLicenses.length > 0 && !selectedLicense) {
            const licenseForCatalog = availableLicenses.find(license =>
                license.catalogRefList?.includes(initialCatalogId)
            );

            if (licenseForCatalog) {
                setSelectedLicense(licenseForCatalog.id);
            }
        }
    }, [initialCatalogId, availableLicenses, setSelectedLicense, selectedLicense]);

    // Function to generate rowKey - stable and simple
    const getRowKey = (record) => {
        // Use a simple identifier based on product codes
        return record.skuCode || record.eanCode || record.upcCode || `item-${Math.random()}`;
    };

    // Funzione per calcolare SKU utilizzati per una subscription
    const calculateSubscriptionSkuUsed = async (catalogRefList) => {
        try {
            if (!catalogRefList || catalogRefList.length === 0) return 0;

            let totalSkuCount = 0;

            // Query CataloguesProducts per ogni catalogo
            for (const catalogRef of catalogRefList) {
                const productsQuery = query(
                    collection(db, "CataloguesProducts"),
                    where("catalogRef", "==", catalogRef)
                );

                const productsSnapshot = await getDocs(productsQuery);

                // Somma tutte le lunghezze di list_variants
                for (const productDoc of productsSnapshot.docs) {
                    const productData = productDoc.data();
                    const variants = productData.list_variants || [];
                    totalSkuCount += variants.length;
                }
            }

            return totalSkuCount;
        } catch (error) {
            console.error('Error calculating subscription SKU used:', error);
            return 0;
        }
    };

    // Funzione per ottenere informazioni sulla subscription
    const getSubscriptionInfo = (subscription, selectedVariantsCount) => {
        const catalogRefList = subscription.catalogRefList || [];
        const skuLimit = subscription.skuLimit || 0;

        // Se skuLimit è 0, significa unlimited
        if (skuLimit === 0) {
            // Controlla se ci sono cataloghi disponibili
            const availableCatalogs = catalogRefList.filter(catalogId => {
                // Controlla se questo catalogo è presente nei catalogsData
                return catalogsData.some(cat => cat.id === catalogId);
            });

            return {
                hasAvailableCatalogs: availableCatalogs.length > 0,
                availableSkus: "No Limits",
                needsUpgrade: false
            };
        }

        const skuData = subscriptionSkuData[subscription.id];
        if (!skuData) {
            return {
                hasAvailableCatalogs: false,
                availableSkus: 0,
                needsUpgrade: false,
                isLoading: true
            };
        }

        const usedSkus = skuData.usedSkus || 0;
        const availableSkus = skuLimit - usedSkus;

        // Controlla se ci sono cataloghi disponibili
        const availableCatalogs = catalogRefList.filter(catalogId => {
            return catalogsData.some(cat => cat.id === catalogId);
        });

        // Controlla se l'aggiunta delle varianti selezionate supererebbe il limite
        const needsUpgrade = selectedVariantsCount > availableSkus;

        return {
            hasAvailableCatalogs: availableCatalogs.length > 0,
            availableSkus: availableSkus,
            needsUpgrade: needsUpgrade
        };
    };

    // Carica le licenze all'avvio
    useEffect(() => {
        loadLicenses();
    }, []);

    // Funzione per caricare le licenze e calcolare gli SKU
    const loadLicenses = async () => {
        setLoadingLicenses(true);
        try {
            const licenses = await fetchAvailableLicenses();

            // Calcola gli SKU utilizzati per ogni subscription
            const skuDataPromises = licenses.map(async (license) => {
                const catalogRefList = license.catalogRefList || [];
                const usedSkus = await calculateSubscriptionSkuUsed(catalogRefList);
                return {
                    id: license.id,
                    usedSkus,
                    catalogRefList
                };
            });

            const skuDataResults = await Promise.all(skuDataPromises);
            const skuDataMap = {};
            skuDataResults.forEach(data => {
                skuDataMap[data.id] = data;
            });

            setSubscriptionSkuData(skuDataMap);
        } catch (error) {
            console.error('Error fetching licenses:', error);
            message.error('Failed to load available licenses.');
        } finally {
            setLoadingLicenses(false);
        }
    };

    // Function to sort licenses by subscription type
    const sortLicensesByType = (licenses) => {
        return [...licenses].sort((a, b) => {
            const getTypeCategory = (subscriptionType) => {
                if (!subscriptionType) return 999;
                const type = subscriptionType.toLowerCase();
                if (type.includes('additional')) return 2;
                if (type.includes('branded')) return 0;
                if (type.includes('smart')) return 1;
                return 999;
            };

            const categoryA = getTypeCategory(a.subscriptionType);
            const categoryB = getTypeCategory(b.subscriptionType);

            if (categoryA === categoryB) {
                return (a.name || '').localeCompare(b.name || '');
            }

            return categoryA - categoryB;
        });
    };

    // Filter and sort licenses - SEMPLIFICATO: solo Active
    const getFilteredLicenses = () => {
        // Mostra solo le licenze Active, senza filtri per tipo
        const filteredLicenses = availableLicenses.filter(license => {
            // Qui potresti aggiungere filtri per status se necessario
            // Per ora mostriamo tutte le licenze disponibili
            return true;
        });

        const sortedLicenses = sortLicensesByType(filteredLicenses);

        return sortedLicenses;
    };

    // Funzione per gestire l'upgrade della subscription
    const handleUpgradeRequest = async () => {
        if (!upgradeSubscription || !upgradeMessage.trim()) {
            message.error('Please enter a message for the upgrade request.');
            return;
        }

        setSendingUpgradeRequest(true);

        try {
            // Ottieni le informazioni del profilo utente
            const userProfile = await getUserProfileForEmail();

            // Invia la richiesta di upgrade
            await sendUpgradeRequestEmail(upgradeSubscription, userProfile, upgradeMessage);

            message.success('Upgrade request sent successfully! You will receive a confirmation email shortly.');
            setUpgradeModalVisible(false);
            setUpgradeMessage('');
            setUpgradeSubscription(null);
        } catch (error) {
            console.error('Error sending upgrade request:', error);
            message.error('Failed to send upgrade request. Please try again.');
        } finally {
            setSendingUpgradeRequest(false);
        }
    };

    // Recupera i cataloghi dal secure storage e da Firestore
    useEffect(() => {
        const loadCatalogsForSubscription = async () => {
            if (!selectedLicense || !availableLicenses) {
                setCatalogsData([]);
                return;
            }

            setLoadingCatalogs(true);
            try {
                // Trova la subscription selezionata
                const selectedSubscription = availableLicenses.find(license => license.id === selectedLicense);
                if (!selectedSubscription || !selectedSubscription.catalogRefList) {
                    setCatalogsData([]);
                    setLoadingCatalogs(false);
                    return;
                }

                // Carica i dettagli dei cataloghi dalla subscription
                const catalogPromises = selectedSubscription.catalogRefList.map(async (catalogId) => {
                    try {
                        const docRef = doc(db, 'Catalogues', catalogId);
                        const docSnap = await getDoc(docRef);

                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            return {
                                id: docSnap.id,
                                nameCatalog: data.nameCatalog || data.name || `Catalog ${docSnap.id.substring(0, 8)}`,
                                name: data.name || data.nameCatalog || `Catalog ${docSnap.id.substring(0, 8)}`
                            };
                        }
                        return null;
                    } catch (error) {
                        console.error(`Error fetching catalog ${catalogId}:`, error);
                        return null;
                    }
                });

                const results = await Promise.all(catalogPromises);
                const validCatalogs = results.filter(c => c !== null);

                setCatalogsData(validCatalogs);
            } catch (error) {
                console.error('Error loading catalogs for subscription:', error);
                message.error('Error loading catalogs');
            } finally {
                setLoadingCatalogs(false);
            }
        };

        loadCatalogsForSubscription();
    }, [selectedLicense, availableLicenses, message]);

    // Effetto per impostare il catalogo nel form
    useEffect(() => {
        // Quando la subscription cambia, aggiorna il campo del form
        if (selectedLicense) {
            form.setFieldsValue({ subscription: selectedLicense });
        }
    }, [selectedLicense, form]);

    useEffect(() => {
        // Quando il catalogo cambia, aggiorna il campo del form
        // Questo non dovrebbe resettare la subscription
        form.setFieldsValue({ catalogue: selectedCatalogue });
    }, [selectedCatalogue, form]);

    // Listener per gli eventi dei cataloghi
    useEffect(() => {
        const handleCatalogPublished = async () => {
            // Aggiungi un piccolo delay per dare tempo al database di sincronizzarsi
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Ricarica i cataloghi per la subscription selezionata
            if (selectedLicense && availableLicenses) {
                const selectedSubscription = availableLicenses.find(license => license.id === selectedLicense);
                if (selectedSubscription && selectedSubscription.catalogRefList) {
                    setLoadingCatalogs(true);
                    try {
                        const catalogPromises = selectedSubscription.catalogRefList.map(async (catalogId) => {
                            try {
                                const docRef = doc(db, 'Catalogues', catalogId);
                                const docSnap = await getDoc(docRef);

                                if (docSnap.exists()) {
                                    const data = docSnap.data();
                                    return {
                                        id: docSnap.id,
                                        nameCatalog: data.nameCatalog || data.name || `Catalog ${docSnap.id.substring(0, 8)}`,
                                        name: data.name || data.nameCatalog || `Catalog ${docSnap.id.substring(0, 8)}`
                                    };
                                }
                                return null;
                            } catch (error) {
                                console.error(`Error fetching catalog ${catalogId}:`, error);
                                return null;
                            }
                        });

                        const results = await Promise.all(catalogPromises);
                        const validCatalogs = results.filter(c => c !== null);
                        setCatalogsData(validCatalogs);
                    } catch (error) {
                        console.error('Error reloading catalogs:', error);
                    } finally {
                        setLoadingCatalogs(false);
                    }
                }
            }
        };

        // Registra il listener
        catalogEvents.on(CATALOG_EVENTS.CATALOG_PUBLISHED, handleCatalogPublished);
        catalogEvents.on(CATALOG_EVENTS.CATALOGS_REFRESHED, handleCatalogPublished);

        // Cleanup function per rimuovere il listener quando il componente viene smontato
        return () => {
            catalogEvents.off(CATALOG_EVENTS.CATALOG_PUBLISHED, handleCatalogPublished);
            catalogEvents.off(CATALOG_EVENTS.CATALOGS_REFRESHED, handleCatalogPublished);
        };
    }, [selectedLicense, availableLicenses]);

    // Prepara le opzioni per il select del catalogo, gestendo il caso in cui il nome non sia ancora caricato
    const catalogOptions = useMemo(() => {
        const filteredAndMapped = catalogsData
            .filter(catalog => {
                if (!selectedLicense) return false;
                const selectedLicenseData = availableLicenses.find(license => license.id === selectedLicense);
                if (!selectedLicenseData) return false;
                const catalogRefList = selectedLicenseData.catalogRefList || [];
                return catalogRefList.includes(catalog.id);
            })
            .map(catalog => ({
                value: catalog.id,
                label: catalog.nameCatalog || catalog.name || 'Unnamed Catalog'
            }));

        // Se un catalogo è selezionato ma non è nella lista (es. dati in caricamento),
        // lo cerchiamo nella lista master per evitare di mostrare solo l'ID.
        if (selectedCatalogue && !filteredAndMapped.some(opt => opt.value === selectedCatalogue)) {
            const catalogFromMasterList = catalogues.find(c => c.id === selectedCatalogue);
            if (catalogFromMasterList) {
                // Inseriamo l'opzione mancante in cima alla lista
                return [
                    {
                        value: catalogFromMasterList.id,
                        label: catalogFromMasterList.nameCatalog || catalogFromMasterList.name || 'Unnamed Catalog'
                    },
                    ...filteredAndMapped
                ];
            }
        }

        return filteredAndMapped;
    }, [catalogsData, selectedCatalogue, selectedLicense, availableLicenses, catalogues]);

    // Controlla se la subscription selezionata ha superato il limite
    const selectedSubscriptionExceedsLimit = useMemo(() => {
        if (!selectedLicense || !availableLicenses.length) return false;

        const subscription = availableLicenses.find(license => license.id === selectedLicense);
        if (!subscription) return false;

        const selectedVariantsCount = selectedRowKeys.length;
        const subscriptionInfo = getSubscriptionInfo(subscription, selectedVariantsCount);

        return subscriptionInfo.needsUpgrade;
    }, [selectedLicense, availableLicenses, selectedRowKeys.length, subscriptionSkuData, catalogsData]);

    // Controlla se la subscription selezionata non ha cataloghi disponibili
    const selectedSubscriptionHasNoCatalogs = useMemo(() => {
        if (!selectedLicense || !availableLicenses.length || loadingCatalogs) return false;

        const subscription = availableLicenses.find(license => license.id === selectedLicense);
        if (!subscription) return false;

        return catalogOptions.length === 0;
    }, [selectedLicense, availableLicenses, catalogOptions.length, loadingCatalogs]);

    // Table columns - memoised
    const columns = useMemo(() => [
        {
            title: 'SKU Code',
            dataIndex: 'skuCode',
            key: 'skuCode',
            width: '25%',
            render: (text) => text || '-',
        },
        {
            title: 'EAN Code',
            dataIndex: 'eanCode',
            key: 'eanCode',
            width: '25%',
            render: (text) => text || '-',
        },
        {
            title: 'UPC Code',
            dataIndex: 'upcCode',
            key: 'upcCode',
            width: '25%',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'result',
            key: 'result',
            width: '25%',
            render: (result) => (
                <span style={{ color: lightBaseTokens.colorSuccess, fontWeight: 'bold' }}>
                    {result}
                </span>
            ),
        },
    ], []);

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => {
            if (onSelectionChange) {
                onSelectionChange(newSelectedRowKeys);
            }
        },
    };

    return (
        <>
            <div style={{ padding: '20px 0' }}>
                <Title level={5}>Select Subscription and Catalogue</Title>
                <Paragraph>
                    First select the subscription, then choose the target catalogue and finally the products to add.
                </Paragraph>

                <Form form={form}>
                    {/* License Selection */}
                    <div style={{ marginBottom: '1rem' }}>
                        <Title level={5}>Select Subscription</Title>
                        <Select
                            placeholder="Select a subscription"
                            style={{ width: '100%' }}
                            value={selectedLicense}
                            onChange={(value) => {
                                setSelectedLicense(value);
                                // Reset catalog selection when license changes
                                setSelectedCatalogue(null);
                                form.setFieldsValue({ catalogue: null, subscription: value });
                            }}
                            loading={loadingLicenses}
                            disabled={!!initialCatalogId}
                            optionLabelProp="label"
                            showSearch
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {getFilteredLicenses().map(subscription => {
                                const selectedVariantsCount = selectedRowKeys.length;
                                const subscriptionInfo = getSubscriptionInfo(subscription, selectedVariantsCount);

                                return (
                                    <Select.Option
                                        key={subscription.id}
                                        value={subscription.id}
                                        label={subscription.name}
                                        disabled={subscriptionInfo.needsUpgrade}
                                    >
                                        <SubscriptionOptionRenderer
                                            subscription={subscription}
                                            subscriptionInfo={subscriptionInfo}
                                            selectedVariantsCount={selectedVariantsCount}
                                            onUpgradeClick={() => {
                                                setUpgradeSubscription(subscription);
                                                setUpgradeModalVisible(true);
                                            }}
                                        />
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>

                    {/* Messaggio di errore per subscription che supera il limite */}
                    {selectedSubscriptionExceedsLimit && (
                        <div style={{
                            marginBottom: '1rem',
                            padding: '1rem',
                            backgroundColor: '#fff2f0',
                            borderRadius: '0.5rem',
                            border: `1px solid ${statusTokens.error}`
                        }}>
                            <Text style={{ fontSize: '0.875rem', color: statusTokens.error }}>
                                ⚠️ This subscription has exceeded its SKU limit. Please request an upgrade to continue.
                            </Text>
                        </div>
                    )}

                    {/* Catalog Selection */}
                    <div style={{ marginBottom: '1rem' }}>
                        <Title level={5}>Available Catalogues</Title>
                        <Select
                            placeholder="Select a catalogue"
                            style={{ width: '100%' }}
                            value={selectedCatalogue}
                            disabled={!selectedLicense || !!initialCatalogId || selectedSubscriptionExceedsLimit || (selectedLicense && catalogOptions.length === 0)}
                            onChange={(value) => {
                                // Non resettare la subscription quando si seleziona un catalogo
                                setSelectedCatalogue(value);
                                form.setFieldsValue({ catalogue: value });
                            }}
                            loading={loadingCatalogs}
                            options={catalogOptions.sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                            optionFilterProp="label"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent={
                                !selectedLicense ?
                                    <span>Please select a subscription first</span> :
                                    selectedSubscriptionExceedsLimit ?
                                        <span>Subscription exceeds SKU limit</span> :
                                        loadingCatalogs ?
                                            <span>Loading catalogues...</span> :
                                            <span>No catalogue found</span>
                            }
                        />
                    </div>
                </Form>

                {/* Messaggio quando una subscription non ha cataloghi disponibili */}
                {selectedSubscriptionHasNoCatalogs && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: greyScaleTokens.grey100,
                        borderRadius: '0.5rem',
                        border: `1px solid ${greyScaleTokens.grey300}`
                    }}>
                        <Text style={{ fontSize: '0.875rem', color: greyScaleTokens.grey700 }}>
                            No catalogue available within this subscription plan. Go to{' '}
                            <Link to="/my-catalogues" style={{ color: primaryShades.primary300 }}>
                                my Catalogue page
                            </Link>{' '}
                            to create one.
                        </Text>
                    </div>
                )}

                {/* Available products table */}
                {availableItemsData.length > 0 && !selectedSubscriptionExceedsLimit && (
                    <div style={{ marginTop: '20px' }}>
                        <Title level={5}>Available Products</Title>
                        <Paragraph>
                            Select the products you want to add to the catalogue. Products marked as "Available" are pre-selected.
                        </Paragraph>

                        <div style={{ marginBottom: '16px' }}>
                            <Text>
                                {availableItemsData.length} products available • {selectedRowKeys.length} selected
                            </Text>
                        </div>

                        <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={availableItemsData}
                            rowKey={getRowKey}
                            pagination={false}
                            scroll={{ y: 400 }}
                            size="small"
                        />
                    </div>
                )}

                {availableItemsData.length === 0 && !summary.details?.orderable?.length && !selectedSubscriptionExceedsLimit && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        <Text>No products to display. Please complete the previous steps first.</Text>
                    </div>
                )}
            </div>

            {/* Modal per richiesta upgrade migliorato */}
            <Modal
                title={
                    <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                        <Title level={4} style={{ margin: 0, color: primaryShades.primary300 }}>
                            Request Subscription Upgrade
                        </Title>
                    </div>
                }
                open={upgradeModalVisible}
                onCancel={() => {
                    setUpgradeModalVisible(false);
                    setUpgradeMessage('');
                    setUpgradeSubscription(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setUpgradeModalVisible(false);
                        setUpgradeMessage('');
                        setUpgradeSubscription(null);
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        loading={sendingUpgradeRequest}
                        onClick={handleUpgradeRequest}
                    >
                        {sendingUpgradeRequest ? 'Sending Request...' : 'Send Request'}
                    </Button>
                ]}
                centered
                width={600}
                maskClosable={!sendingUpgradeRequest}
                closable={!sendingUpgradeRequest}
            >
                <div style={{ padding: '20px 0' }}>
                    {/* Informazioni sulla subscription */}
                    <div style={{
                        backgroundColor: primaryShades.primary100,
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: `1px solid ${primaryShades.primary200}`
                    }}>
                        <Title level={5} style={{ margin: '0 0 12px 0', color: primaryShades.primary600 }}>
                            Subscription Details
                        </Title>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                                <Text strong>Name: </Text>
                                <Text>{upgradeSubscription?.name}</Text>
                            </div>
                            <div>
                                <Text strong>Type: </Text>
                                <Text>{upgradeSubscription?.subscriptionType}</Text>
                            </div>
                            <div>
                                <Text strong>Current SKU Limit: </Text>
                                <Text style={{ color: statusTokens.error, fontWeight: 'bold' }}>
                                    {upgradeSubscription?.skuLimit === 0 ? 'Unlimited' : upgradeSubscription?.skuLimit}
                                </Text>
                            </div>
                            <div>
                                <Text strong>Status: </Text>
                                <Text style={{ color: statusTokens.error, fontWeight: 'bold' }}>Limit Exceeded</Text>
                            </div>
                        </div>
                    </div>

                    {/* Messaggio di spiegazione */}
                    <div style={{
                        backgroundColor: '#fff7e6',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: `1px solid ${statusTokens.warning}`
                    }}>
                        <Text style={{ color: statusTokens.warning }}>
                            <strong>⚠️ SKU Limit Exceeded</strong>
                        </Text>
                        <br />
                        <Text style={{ color: greyScaleTokens.grey700 }}>
                            You are trying to add more products than your current subscription allows.
                            Please describe your requirements below and our team will review your upgrade request.
                        </Text>
                    </div>

                    {/* Campo per il messaggio */}
                    <div>
                        <Title level={5} style={{ marginBottom: '8px' }}>
                            Describe your upgrade requirements:
                        </Title>
                        <TextArea
                            rows={4}
                            value={upgradeMessage}
                            onChange={(e) => setUpgradeMessage(e.target.value)}
                            placeholder="Please describe:
• How many additional SKUs you need
• Your business requirements
• Timeline for the upgrade
• Any other relevant information..."
                            maxLength={1000}
                            showCount
                            style={{ marginBottom: '12px' }}
                        />
                        <Text style={{ fontSize: '12px', color: greyScaleTokens.grey600 }}>
                            This request will be sent to our support team
                        </Text>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default SelectAddStep; 